import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import RequirementsPanel from '../components/RequirementsPanel';
import DesignCanvas from '../components/DesignCanvas';
import ChatBot from '../components/ChatBot';
import Header from '../components/Header';
import AuthModal from '../components/AuthModal';
import SkeletonLoader from '../components/SkeletonLoader';
import WelcomeBanner from '../components/WelcomeBanner';
import ProductTour from '../components/ProductTour';
import FeedbackModal from '../components/FeedbackModal';
import apiClient from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import './DesignPage.css';

// Check if ChatBot feature is enabled via environment variable
const ENABLE_CHATBOT = process.env.REACT_APP_ENABLE_CHATBOT === 'true';

function DesignPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canvasRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [needUserKey, setNeedUserKey] = useState(false);
  const [designData, setDesignData] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loadingDesign, setLoadingDesign] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const { isAuthenticated } = useAuth();

  // Auto-save states
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  const [lastSavedState, setLastSavedState] = useState(null);
  const autoSaveTimerRef = useRef(null);

  // Track first draw for welcome banner
  const [hasDrawn, setHasDrawn] = useState(false);

  // Tour states
  const [runTour, setRunTour] = useState(false);
  const TOUR_COMPLETED_KEY = 'systemcraft_tour_completed';
  const TOUR_SKIPPED_KEY = 'systemcraft_tour_skipped';

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Load questions on mount and handle query params
  useEffect(() => {
    const loadQuestions = async () => {
      setLoadingQuestions(true);
      try {
        const result = await apiClient.getQuestions();
        setQuestions(result.questions || []);

        const questionId = searchParams.get('question');

        let questionToSelect = null;
        if (questionId && result.questions) {
          questionToSelect = result.questions.find(q => q.id === questionId);
        }

        // If no question found, redirect to challenge list
        if (!questionToSelect) {
          navigate('/');
          return;
        }

        setSelectedQuestion(questionToSelect);
      } catch (err) {
        toast.error('Failed to load challenge');
        navigate('/');
      } finally {
        setLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, [searchParams, navigate]);

  // Load latest evaluation when question is selected
  useEffect(() => {
    const loadLatestEvaluation = async () => {
      if (!selectedQuestion || !isAuthenticated) {
        setEvaluation(null);
        return;
      }

      // Clear previous evaluation first
      setEvaluation(null);

      try {
        const latestEval = await apiClient.getLatestEvaluation(selectedQuestion.id);

        if (latestEval && latestEval.result) {
          setEvaluation(latestEval.result);
          // Auto-expand right panel to show evaluation
          setRightPanelCollapsed(false);
        }
      } catch {
        // Ignore; no previous evaluation
      }
    };

    loadLatestEvaluation();
  }, [selectedQuestion, isAuthenticated]);

  // Auto-save function with change detection
  const autoSave = useCallback(async () => {
    if (!isAuthenticated || !selectedQuestion || !canvasRef.current) {
      return;
    }

    try {
      const jsonExport = canvasRef.current?.exportAsJson?.();

      if (!jsonExport || !jsonExport.json) {
        return; // Nothing to save
      }

      // Check if design has actually changed
      const currentState = JSON.stringify(jsonExport.json);
      if (currentState === lastSavedState) {
        return; // No changes, skip save
      }

      setAutoSaveStatus('saving');

      await apiClient.saveDesign({
        mode: 'json',
        designData: jsonExport.json,
        questionId: selectedQuestion.id,
      });

      setLastSavedState(currentState);
      setAutoSaveStatus('saved');
    } catch (err) {
      setAutoSaveStatus('unsaved');
    }
  }, [isAuthenticated, selectedQuestion, lastSavedState]);

  // Handle canvas changes for auto-save
  const handleCanvasChange = useCallback(() => {
    if (!isAuthenticated || !selectedQuestion) {
      return;
    }

    // Mark that user has started drawing (for welcome banner)
    if (!hasDrawn) {
      setHasDrawn(true);
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Mark as unsaved
    setAutoSaveStatus('unsaved');

    // Schedule auto-save after 5 seconds of inactivity
    autoSaveTimerRef.current = setTimeout(() => {
      autoSave();
    }, 5000);
  }, [isAuthenticated, selectedQuestion, autoSave, hasDrawn]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Auto-start tour for first-time users
  useEffect(() => {
    if (!selectedQuestion) return;

    const tourCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);
    const tourSkipped = localStorage.getItem(TOUR_SKIPPED_KEY);

    // Auto-start tour for first-time users after 2 seconds
    if (!tourCompleted && !tourSkipped) {
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [selectedQuestion]);

  // Tour handlers
  const handleStartTour = () => {
    setRunTour(true);
  };

  const handleTourComplete = () => {
    setRunTour(false);
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    toast.success('Tour completed! Ready to design?', { duration: 3000 });
  };

  const handleTourSkip = () => {
    setRunTour(false);
    localStorage.setItem(TOUR_SKIPPED_KEY, 'true');
  };

  const handleFeedback = () => {
    setShowFeedbackModal(true);
  };


  const handleQuestionChange = useCallback((question) => {
    setSelectedQuestion(question);
    if (question) {
      navigate(`/design?question=${question.id}`);
    }
  }, [navigate]);

  const saveDraft = useCallback(async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!selectedQuestion) {
      toast.error('Please select a question first.');
      return;
    }

    setSavingDraft(true);
    setAutoSaveStatus('saving');

    try {
      const jsonExport = canvasRef.current?.exportAsJson?.();

      if (!jsonExport || !jsonExport.json) {
        toast.error('Please draw your system design on the canvas first.');
        return;
      }

      const saveResult = await apiClient.saveDesign({
        mode: 'json',
        designData: jsonExport.json,
        questionId: selectedQuestion.id,
      });

      // Update last saved state
      setLastSavedState(JSON.stringify(jsonExport.json));
      setAutoSaveStatus('saved');
    } catch (err) {
      toast.error('Failed to save draft: ' + (err.message || 'Unknown error'));
      setAutoSaveStatus('unsaved');
    } finally {
      setSavingDraft(false);
    }
  }, [isAuthenticated, selectedQuestion]);

  // Keyboard shortcut for Ctrl+S to save draft
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S on Mac (check both lowercase and uppercase)
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        e.stopPropagation();
        saveDraft();
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [saveDraft]);

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    setEvaluation(null);
    setError(null);

    if (!selectedQuestion) {
      toast.error('Please select a question first.');
      return;
    }

    try {
      const jsonExport = canvasRef.current?.exportAsJson?.();

      if (!jsonExport || !jsonExport.json) {
        toast.error('Please draw your system design on the canvas first.');
        return;
      }

      const elements = jsonExport.json.elements || [];
      const meaningfulElements = elements.filter(el => 
        el.type !== 'selection' && 
        !el.isDeleted &&
        el.type !== 'freedraw'
      );
      
      const shapes = meaningfulElements.filter(el => 
        ['rectangle', 'ellipse', 'diamond'].includes(el.type)
      );
      const textElements = meaningfulElements.filter(el => el.type === 'text');
      const arrows = meaningfulElements.filter(el => el.type === 'arrow' || el.type === 'line');
      
      const hasMinimumContent = shapes.length >= 3 || 
        (shapes.length >= 2 && (textElements.length >= 1 || arrows.length >= 1));
      
      if (!hasMinimumContent) {
        toast.error('Your design needs more components. Please add at least 3 system components (boxes/shapes) with labels or connections.');
        return;
      }

      const saveResult = await apiClient.saveDesign({
        mode: 'json',
        designData: jsonExport.json,
        questionId: selectedQuestion.id,
      });

      const imageDataUrl = await canvasRef.current?.exportAsPng?.();

      const savedData = {
        mode: 'image',
        imageData: imageDataUrl,
        designData: jsonExport.json,
        designId: saveResult.design_id,
        questionId: selectedQuestion.id,
      };

      setDesignData(savedData);
      setRightPanelCollapsed(false);
      setLoading(true);

      try {
        const result = await apiClient.evaluateDesign({
          mode: savedData.mode,
          imageData: savedData.imageData,
          designData: savedData.designData,
          questionId: savedData.questionId,
          designId: savedData.designId || null,
        });

        setEvaluation(result);

        if (savedData.designId) {
          try {
            const freshJson = canvasRef.current?.exportAsJson?.();
            await apiClient.saveEvaluation(
              savedData.designId,
              savedData.questionId,
              result,
              freshJson?.json
            );
          } catch {
            // Ignore save evaluation errors
          }
        }
      } catch (err) {
        setError(err.message || 'Failed to evaluate. Please try again.');
        if (err.requireUserKey) setNeedUserKey(true);
      } finally {
        setLoading(false);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save design. Please try again.');
    }
  };

  const handleBackToChallenges = () => {
    navigate('/');
  };

  return (
    <div className="design-page">
      <Header
        onSubmit={handleSubmit}
        onSaveDraft={saveDraft}
        onBack={handleBackToChallenges}
        selectedQuestion={selectedQuestion}
        savingDraft={savingDraft}
        onFeedback={handleFeedback}
      />
      <main className="main-content">
        {/* Left Panel */}
        <div className={`panel panel-left animate-fade-in animate-delay-1 ${leftPanelCollapsed ? 'collapsed' : ''}`}>
          {!leftPanelCollapsed && (
            <>
              {loadingQuestions ? (
                <SkeletonLoader type="requirements-panel" />
              ) : (
                <RequirementsPanel
                  questions={questions}
                  selectedQuestion={selectedQuestion}
                  onQuestionChange={handleQuestionChange}
                />
              )}
            </>
          )}
        </div>

        {/* Left Toggle Button */}
        <button
          className={`panel-toggle panel-toggle-left ${!leftPanelCollapsed ? 'expanded' : ''}`}
          onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          title={leftPanelCollapsed ? "Show Requirements" : "Hide Requirements"}
        >
          {leftPanelCollapsed ? '›' : '‹'}
        </button>

        {/* Center Panel - Canvas */}
        <div className="panel panel-center animate-fade-in animate-delay-2">
          <WelcomeBanner
            onFirstDraw={hasDrawn}
            selectedQuestion={selectedQuestion}
            onStartTour={handleStartTour}
          />
          <DesignCanvas
            ref={canvasRef}
            selectedQuestion={selectedQuestion}
            onChange={handleCanvasChange}
          />
        </div>

        {/* Right Toggle Button */}
        <button
          className={`panel-toggle panel-toggle-right ${!rightPanelCollapsed ? 'expanded' : ''}`}
          onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
          title={rightPanelCollapsed ? "Show Suggestions" : "Hide Suggestions"}
        >
          {rightPanelCollapsed ? '‹' : '›'}
        </button>

        {/* Right Panel */}
        <div
          className={`panel panel-right animate-fade-in animate-delay-3 ${rightPanelCollapsed ? 'collapsed' : ''}`}
        >
          {!rightPanelCollapsed && (
            <ChatBot
              evaluation={evaluation}
              evaluationLoading={loading}
              evaluationError={error}
              enableChatbot={ENABLE_CHATBOT}
            />
          )}
        </div>
      </main>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      <ProductTour
        run={runTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
}

export default DesignPage;
